import React, { useRef } from "react";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { Phone, ArrowDown } from "lucide-react";
import { IMAGES, BRAND } from "../../data/content";
import { useOrder } from "../../context/OrderContext";
import { MaskReveal, EASE } from "./Reveal";

export const Hero = () => {
  const { openOrder } = useOrder();
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", reduce ? "0%" : "22%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1.05, reduce ? 1.05 : 1.2]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", reduce ? "0%" : "-18%"]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  return (
    <section ref={ref} id="top" className="relative h-[100svh] min-h-[640px] w-full overflow-hidden grain">
      {/* Parallax background */}
      <motion.div className="absolute inset-0" style={{ y: imgY, scale: imgScale }}>
        <img
          src={IMAGES.jalapenoBurger}
          alt="Smokey & Creamy signature burger"
          className="h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/55 to-ink/40" />
        <div className="absolute inset-0 bg-gradient-to-r from-ink/85 via-transparent to-transparent" />
      </motion.div>

      {/* Content */}
      <motion.div
        style={{ y: contentY, opacity: contentOpacity }}
        className="relative z-10 mx-auto flex h-full max-w-7xl flex-col justify-end px-5 pb-16 sm:px-8 sm:pb-20"
      >
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: EASE, delay: 0.15 }}
          className="mb-5 flex flex-wrap items-center gap-2.5"
        >
          <span className="inline-flex items-center gap-2 rounded-full bg-gold/15 border border-gold/30 px-3.5 py-1.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-gold">
            100% Halal Menu
          </span>
          <span className="inline-flex items-center rounded-full border border-white/15 bg-white/5 px-3.5 py-1.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-cream/70">
            Winnipeg · Open Daily
          </span>
        </motion.div>

        <h1 className="font-display font-semibold tracking-tight text-cream text-[15vw] leading-[0.88] sm:text-8xl lg:text-[8.5rem]">
          <MaskReveal lines={["Smoked.", "Stacked.", <span key="c" className="text-gold-gloss">Creamy.</span>]} delay={0.2} trigger="load" />
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: EASE, delay: 0.7 }}
          className="mt-6 max-w-xl text-base sm:text-lg text-cream/75 leading-relaxed"
        >
          Winnipeg's 100% Halal burger, philly &amp; taco house. Made fresh to order,
          served loud — the kind of food you cross town for.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: EASE, delay: 0.85 }}
          className="mt-8 flex flex-wrap items-center gap-3"
        >
          <button
            onClick={openOrder}
            data-testid="hero-order-now"
            className="inline-flex items-center rounded-full bg-gold px-7 py-4 text-base font-semibold text-ink transition-transform hover:-translate-y-0.5 gold-glow"
          >
            Order Now
          </button>
          <a
            href={BRAND.phoneHref}
            data-testid="hero-call"
            className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/5 px-6 py-4 text-base font-semibold text-cream backdrop-blur-sm transition-colors hover:border-gold/40 hover:text-gold"
          >
            <Phone size={18} /> {BRAND.phoneDisplay}
          </a>
        </motion.div>
      </motion.div>

      {/* Scroll cue */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4 }}
        className="absolute bottom-6 right-6 z-10 hidden sm:flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-cream/50"
      >
        Scroll
        <motion.span animate={reduce ? {} : { y: [0, 6, 0] }} transition={{ repeat: Infinity, duration: 1.6 }}>
          <ArrowDown size={14} />
        </motion.span>
      </motion.div>
    </section>
  );
};

export default Hero;
